/**
 * usePlaylistManager.js
 *
 * Extracted from PlayerContext.js - handles all playlist manipulation:
 * adding/removing/reordering tracks, normalization, playlist creation, and restoration.
 *
 * Provides: setPlaylist, addTrackToPlaylist, addTracksToPlaylist, playTrack,
 *           playTrackNext, reorderPlaylistTracks, removeTrackFromPlaylist,
 *           clearPlaylist, createPlaylistFromTracks, restorePreviousPlaylist,
 *           normalizeTrackFormat, updatePlaylistTracks
 */
import { useCallback } from 'react';
import logger from '../services/logger';
import { logUserAction } from '../utils/basicBehaviorLogger';
import api from '../services/api';

const ActionTypes = {
  SET_PLAYLIST: 'SET_PLAYLIST',
  UPDATE_PLAYLIST: 'UPDATE_PLAYLIST',
  REORDER_PLAYLIST: 'REORDER_PLAYLIST'
};

// Utility function for playlist management
const createUpdatedTracksArray = (tracks) => [...tracks];

export function usePlaylistManager(state, dispatch, refs) {
  const {
    radioModeRef,
    setCurrentTrackIndex,
    setPlaybackState,
    clearDJCache
  } = refs;

  const setPlaylist = useCallback((playlist, options = {}) => {
    dispatch({
      type: ActionTypes.SET_PLAYLIST,
      payload: {
        playlist,
        savePrevious: options.savePrevious || false,
        resetIndex: options.resetIndex !== false,
        resetProgress: options.resetProgress !== false
      }
    });
  }, [dispatch]);

  const updatePlaylistTracks = useCallback((tracks) => {
    dispatch({
      type: ActionTypes.UPDATE_PLAYLIST,
      payload: { tracks }
    });
  }, [dispatch]);

  // Track format normalization
  const normalizeTrackFormat = useCallback((track) => {
    try {
      if (!track || typeof track !== 'object') {
        logger.error("Cannot normalize undefined or non-object track:", track);
        return null;
      }

      if (!track.name && !track.uri) {
        logger.error("Track missing required properties (name or uri):", track);
        return null;
      }

      let artistsString = 'Unknown Artist';
      try {
        if (typeof track.artists === 'string') {
          artistsString = track.artists;
        } else if (Array.isArray(track.artists)) {
          artistsString = track.artists
            .map(a => (a && typeof a === 'object' && a.name) ? a.name : (a || ''))
            .filter(Boolean)
            .join(', ');
        } else if (track.artist) {
          artistsString = track.artist;
        }
      } catch (artistErr) {
        logger.warn("Error processing track artists, using default:", artistErr);
      }

      let images = [{ url: '/default-album.png' }];
      try {
        if (Array.isArray(track.images) && track.images.length > 0) {
          images = [...track.images];
        } else if (track.album && Array.isArray(track.album.images) && track.album.images.length > 0) {
          images = [...track.album.images];
        }
      } catch (imageErr) {
        logger.warn("Error processing track images, using default:", imageErr);
      }

      return {
        uri: track.uri || '',
        name: track.name || 'Unknown Track',
        artists: artistsString,
        images: images,
        _uniqueId: track._uniqueId || `track-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
      };
    } catch (error) {
      logger.error("Error in normalizeTrackFormat:", error);
      return null;
    }
  }, []);

  const addTrackToPlaylist = useCallback((track, position = 'end') => {
    if (!track) return;

    // Check transition lock for 'next' position (play next from search)
    if (position === 'next' && radioModeRef.current?.services?.control) {
      const blockInfo = radioModeRef.current.services.control.shouldBlockPlaylistAction('play_next', state.currentTrackIndex + 1);
      if (blockInfo.blocked) {
        logger.debug(`🔒 Play next blocked: ${blockInfo.reason}`);
        return;
      }
    }

    const formattedTrack = normalizeTrackFormat(track);
    if (!formattedTrack) return;

    const updatedTracks = createUpdatedTracksArray(state.playlist.tracks);
    let insertIndex;

    if (position === 'next') {
      insertIndex = state.currentTrackIndex + 1;
      updatedTracks.splice(insertIndex, 0, formattedTrack);
    } else if (position === 'end') {
      insertIndex = updatedTracks.length;
      updatedTracks.push(formattedTrack);
    } else if (typeof position === 'number') {
      insertIndex = position;
      updatedTracks.splice(position, 0, formattedTrack);
    }

    // If inserting before or at next-track slot, the cached DJ preload is now for the wrong song — discard it
    if (position === 'next' && clearDJCache) {
      clearDJCache();
      logger.debug('[Playlist] clearDJCache — track inserted at next, stale DJ preload discarded');
    }

    // Dispatch with addedAtIndex so reducer can adjust placard positions
    dispatch({
      type: ActionTypes.UPDATE_PLAYLIST,
      payload: { tracks: updatedTracks, addedAtIndex: insertIndex, addedCount: 1 }
    });

    // PHASE 0: Log track addition action
    logUserAction({
      action: 'add',
      trackUri: formattedTrack.uri,
      trackName: formattedTrack.name,
      artist: formattedTrack.artists || 'Unknown Artist',
      position: null,
      timestamp: Date.now(),
      metadata: { addPosition: position }
    });

    logger.platform(`Added track "${formattedTrack.name}" to playlist (${position})`);
    return formattedTrack;
  }, [state.playlist, state.currentTrackIndex, dispatch, normalizeTrackFormat, radioModeRef, clearDJCache]);

  const addTracksToPlaylist = useCallback((tracks, position = 'end') => {
    if (!tracks || tracks.length === 0) return;

    const formattedTracks = tracks.map(track => normalizeTrackFormat(track)).filter(Boolean);
    if (formattedTracks.length === 0) return;

    // APPEND_TRACKS: reducer reads current state.playlist.tracks at dispatch time.
    // This avoids stale closure bugs where the callback closed over an old tracks array
    // (e.g. when generateMore fires asynchronously after setPlaylist was dispatched but
    // React hasn't re-rendered yet — the old closure would wipe the freshly-loaded playlist).
    dispatch({
      type: 'APPEND_TRACKS',
      payload: { tracks: formattedTracks, position }
    });

    logger.platform(`Added ${formattedTracks.length} tracks to playlist (${position})`);
    return formattedTracks;
  }, [dispatch, normalizeTrackFormat]); // state.playlist/currentTrackIndex removed — reducer owns that logic

  const playTrack = useCallback((track) => {
    if (!track) return;

    const formattedTrack = normalizeTrackFormat(track);
    if (!formattedTrack) return;

    const newPlaylist = {
      name: `Playing: ${formattedTrack.name}`,
      tracks: [formattedTrack]
    };

    setPlaylist(newPlaylist, { savePrevious: true });
    setCurrentTrackIndex(0);
    setPlaybackState(true);

    // Listening history now handled by useListeningTracker (tracks real listen duration)
    const recordPlay = () => {
      logger.debug('[recordPlay] deprecated — useListeningTracker handles listen history');
    };
    recordPlay();

  }, [setPlaylist, setCurrentTrackIndex, setPlaybackState, normalizeTrackFormat, state.platform]);

  const playTrackNext = useCallback((track) => {
    return addTrackToPlaylist(track, 'next');
  }, [addTrackToPlaylist]);

  const reorderPlaylistTracks = useCallback((fromIndex, toIndex) => {
    if (!state.playlist.tracks) return;

    // FIX: Check transition lock - block moves involving or crossing locked tracks
    if (radioModeRef.current?.services?.control) {
      const lock = radioModeRef.current.services.control.transitionLock;

      if (lock) {
        const { currentIndex, nextIndex } = lock;
        const minLocked = Math.min(currentIndex, nextIndex);
        const maxLocked = Math.max(currentIndex, nextIndex);

        // Block if move directly involves locked tracks
        if (fromIndex === currentIndex || fromIndex === nextIndex ||
            toIndex === currentIndex || toIndex === nextIndex) {
          logger.debug(`🔒 Reorder blocked: Cannot move locked tracks during DJ transition`);
          return;
        }

        // Block if move would shift the locked tracks (crosses the locked range)
        const moveMin = Math.min(fromIndex, toIndex);
        const moveMax = Math.max(fromIndex, toIndex);
        if (moveMin < minLocked && moveMax >= minLocked) {
          logger.debug(`🔒 Reorder blocked: Move would shift locked tracks during DJ transition`);
          return;
        }
      }
    }

    const updatedTracks = createUpdatedTracksArray(state.playlist.tracks);
    const [movedTrack] = updatedTracks.splice(fromIndex, 1);
    updatedTracks.splice(toIndex, 0, movedTrack);

    // Calculate new index for current track
    let newIndex = state.currentTrackIndex;

    if (state.currentTrackIndex === fromIndex) {
      newIndex = toIndex;
    } else if (
      (fromIndex < state.currentTrackIndex && toIndex >= state.currentTrackIndex) ||
      (fromIndex > state.currentTrackIndex && toIndex <= state.currentTrackIndex)
    ) {
      newIndex = state.currentTrackIndex + (fromIndex < toIndex ? -1 : 1);
    }

    // FIX: Single atomic dispatch updates both playlist AND index + placard positions
    dispatch({
      type: ActionTypes.REORDER_PLAYLIST,
      payload: {
        tracks: updatedTracks,
        newIndex: newIndex,
        fromIndex: fromIndex,
        toIndex: toIndex,
        preserveProgress: true
      }
    });
  }, [state.playlist, state.currentTrackIndex, dispatch, radioModeRef]);

  const removeTrackFromPlaylist = useCallback((index) => {
    if (index < 0 || index >= state.playlist.tracks.length) return;

    // FIX: Check transition lock - block removes of locked tracks or above them
    if (radioModeRef.current?.services?.control) {
      const lock = radioModeRef.current.services.control.transitionLock;

      if (lock) {
        const { currentIndex, nextIndex } = lock;

        if (index === currentIndex || index === nextIndex) {
          logger.debug(`🔒 Remove blocked: Cannot remove locked track during DJ transition`);
          return;
        }

        if (index < Math.min(currentIndex, nextIndex)) {
          logger.debug(`🔒 Remove blocked: Would shift locked tracks during DJ transition`);
          return;
        }
      }
    }

    // PHASE 0: Log track removal action
    const removedTrack = state.playlist.tracks[index];
    if (removedTrack) {
      logUserAction({
        action: 'remove',
        trackUri: removedTrack.uri,
        trackName: removedTrack.name,
        artist: removedTrack.artists || 'Unknown Artist',
        position: null,
        timestamp: Date.now()
      });
    }

    const updatedTracks = createUpdatedTracksArray(state.playlist.tracks);
    updatedTracks.splice(index, 1);

    let newCurrentTrackIndex = state.currentTrackIndex;
    if (index < state.currentTrackIndex) {
      newCurrentTrackIndex = state.currentTrackIndex - 1;
    } else if (index === state.currentTrackIndex) {
      if (updatedTracks.length === 0) {
        setPlaybackState(false);
      } else if (index >= updatedTracks.length) {
        newCurrentTrackIndex = Math.max(0, updatedTracks.length - 1);
      }
    }

    // Dispatch with removedIndex so reducer can adjust placard positions
    dispatch({
      type: ActionTypes.UPDATE_PLAYLIST,
      payload: { tracks: updatedTracks, removedIndex: index }
    });

    if (newCurrentTrackIndex !== state.currentTrackIndex) {
      setCurrentTrackIndex(newCurrentTrackIndex);
    }
  }, [state.playlist, state.currentTrackIndex, dispatch, setCurrentTrackIndex, setPlaybackState, radioModeRef]);

  const clearPlaylist = useCallback(() => {
    setPlaylist({
      name: 'Empty Playlist',
      tracks: []
    });
    setPlaybackState(false);
    setCurrentTrackIndex(0);
  }, [setPlaylist, setPlaybackState, setCurrentTrackIndex]);

  const createPlaylistFromTracks = useCallback((tracks, playlistName = 'New Playlist') => {
    if (!tracks || tracks.length === 0) return;

    const formattedTracks = tracks.map(track => normalizeTrackFormat(track)).filter(Boolean);
    if (formattedTracks.length === 0) return;

    setPlaylist({
      name: playlistName,
      tracks: formattedTracks
    }, { savePrevious: true });

    setCurrentTrackIndex(0);

    if (!state.isPlaying) {
      setPlaybackState(true);
    }
  }, [setPlaylist, setCurrentTrackIndex, setPlaybackState, normalizeTrackFormat, state.isPlaying]);

  const restorePreviousPlaylist = useCallback(() => {
    if (state.previousPlaylist) {
      setPlaylist(state.previousPlaylist, { resetIndex: true, resetProgress: true });
    }
  }, [state.previousPlaylist, setPlaylist]);

  return {
    setPlaylist,
    updatePlaylistTracks,
    normalizeTrackFormat,
    addTrackToPlaylist,
    addTracksToPlaylist,
    playTrack,
    playTrackNext,
    reorderPlaylistTracks,
    removeTrackFromPlaylist,
    clearPlaylist,
    createPlaylistFromTracks,
    restorePreviousPlaylist
  };
}
